import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { agentsApi } from '@/lib/agents';
import { AppShell } from '@/components/app/AppShell';
import { Dropdown } from '@/components/ui/Dropdown';
import { OutputLanguageSelect } from '@/components/app/OutputLanguageSelect';
import { HelpHint } from '@/components/app/HelpHint';
import { ExampleChips } from '@/components/app/ExampleChips';
import { AUDIENCE_PRESETS } from '@/lib/agentHelp';
import { CitedMarkdown } from '@/components/ui/CitedMarkdown';
import { modelTierLabel } from '@/lib/model-label';
import { SaveResultButton } from '@/components/app/SaveResultButton';
import { ResultFooter } from '@/components/app/ResultFooter';
import { LanguageCode, defaultOutputLanguage } from '@/i18n/languages';

export function BriefPage() {
  const { t, i18n } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const [topic, setTopic] = useState('');
  const [audience, setAudience] = useState('');
  const [language, setLanguage] = useState<LanguageCode>(defaultOutputLanguage(i18n.language));
  const [length, setLength] = useState<'short' | 'medium' | 'long'>('medium');

  const brief = useMutation({
    mutationFn: () => agentsApi.brief(wsId!, { topic, audience, language, length }),
  });

  return (
    <AppShell
      title={t('brief_agent.title', { defaultValue: 'Brief' })}
      description={t('brief_agent.subtitle', { defaultValue: 'Synthesize a focused briefing from your indexed knowledge.' }) as string}
      maxWidth="max-w-4xl"
    >
      <div className="space-y-5">
        <HelpHint page="brief" />
        <div>
          <label className="block text-sm font-medium text-ink-900 mb-1">{t('brief_agent.topic', { defaultValue: 'Topic' })}</label>
          <input
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (topic.trim() && audience.trim() && !brief.isPending) brief.mutate();
              }
            }}
            placeholder={t('brief_agent.topic_placeholder', { defaultValue: 'What is the brief about?' }) as string}
            className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
          />
          {!topic.trim() && (
            <div className="mt-3">
              <ExampleChips page="brief" onPick={setTopic} />
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-ink-900 mb-1">{t('brief_agent.audience', { defaultValue: 'Audience' })}</label>
          <input
            value={audience}
            onChange={(e) => setAudience(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (topic.trim() && audience.trim() && !brief.isPending) brief.mutate();
              }
            }}
            placeholder={t('brief_agent.audience_placeholder', { defaultValue: 'Who is reading this? Pick one below or type your own.' }) as string}
            className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
          />
          <div className="mt-2">
            <ExampleChips
              items={AUDIENCE_PRESETS}
              label={t('help.common_audiences', { defaultValue: 'Common audiences' }) as string}
              onPick={setAudience}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-ink-900 mb-1">{t('brief_agent.length', { defaultValue: 'Length' })}</label>
            <div className="w-full">
              <Dropdown<'short' | 'medium' | 'long'>
                value={length}
                onChange={(next) => setLength(next)}
                items={(['short', 'medium', 'long'] as const).map((l) => ({
                  value: l,
                  label: t(`brief_agent.length.${l}`, { defaultValue: l }) as string,
                }))}
                ariaLabel={t('brief_agent.length', { defaultValue: 'Length' }) as string}
                size="md"
                className="w-full"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-900 mb-1">{t('brief_agent.language', { defaultValue: 'Output language' })}</label>
            <div className="w-full">
              <OutputLanguageSelect value={language} onChange={setLanguage} />
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={() => brief.mutate()}
            disabled={!topic.trim() || !audience.trim() || brief.isPending}
            className="btn-press px-4 py-2 text-sm disabled:opacity-40"
          >
            {brief.isPending ? t('brief_agent.synthesizing', { defaultValue: 'Synthesizing…' }) : t('brief_agent.submit', { defaultValue: 'Synthesize' })}
          </button>
        </div>

        {brief.data && (
          <>
            {brief.data.outline.length > 0 && (
              <section className="card-paper p-5">
                <h3 className="text-sm font-semibold text-ink-900 mb-2">{t('brief_agent.outline', { defaultValue: 'Outline' })}</h3>
                <ol className="list-decimal list-inside text-sm space-y-1 text-ink-700">
                  {brief.data.outline.map((o, i) => (
                    <li key={i}>{o}</li>
                  ))}
                </ol>
              </section>
            )}
            <section className="card-paper p-5">
              <h3 className="text-sm font-semibold text-ink-900 mb-2">{t('brief_agent.body', { defaultValue: 'Body' })}</h3>
              <CitedMarkdown content={brief.data.body} wsId={wsId} />
              <div className="mt-3 text-xs text-ink-500">
                {modelTierLabel(brief.data.model)} · {brief.data.inputTokens + brief.data.outputTokens} tok · ¢{brief.data.costCents}
              </div>
              <ResultFooter wsId={wsId!} citedChunkIds={brief.data.citedChunkIds} askSeed={topic}>
                <SaveResultButton
                  workspaceId={wsId!}
                  kind="brief"
                  title={topic}
                  output={brief.data.body}
                  input={{ topic, audience, language, length }}
                  citedChunkIds={brief.data.citedChunkIds}
                  model={brief.data.model}
                  costCents={brief.data.costCents}
                />
              </ResultFooter>
            </section>
          </>
        )}
      </div>
    </AppShell>
  );
}
